import { Link } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { ScrollTrigger } from "gsap/ScrollTrigger";
import retailSquare from './../../assets/images/retail-shapes-square.svg';
import retailCircle from './../../assets/images/retail-shapes-circle.svg';
import retailTriangle from './../../assets/images/retail-shapes-triangle.svg';
import retailMetricsBarChart from './../../assets/images/retail-metrics-bar-chart.svg';
import './../retail.scss';
import { gsap } from 'gsap';
import AOS from "aos";
import "aos/dist/aos.css";

export default function RetailMetrics() {

  const squareRef = useRef();
  const triangle1Ref = useRef();
  const triangle2Ref = useRef();

  gsap.registerPlugin(ScrollTrigger);

  useEffect(() => {

    document.addEventListener('aos:in:bar-anim', function(ev) {
      //console.log('animated in', ev);

      if (ev.detail.querySelectorAll('.progress').length > 1) {
        //console.log("more than 1 progress");
        var barArray = [...ev.detail.querySelectorAll('.progress')];
      } else {
        //console.log("less than 1 progress");
        var barArray = [];
        var bar = ev.detail.querySelector('.progress');
        //console.log(bar);
        barArray.push(bar);
      }

      //console.log(barArray);

      if (barArray && barArray.length) {
        barArray.forEach(function(barItem) {
          var fill = barItem.getAttribute('data-fill');

          if (ev.detail.classList.contains('vertical-bar-chart')) {
            barItem.style.height = fill + '%';
          }

          if (!ev.detail.classList.contains('vertical-bar-chart')) {
            barItem.style.width = fill + '%';
          }

        });
      }

      var Cont={val:0};

      if (ev.detail.querySelectorAll('.percentage').length > 1) {
        var percentageArray = [...ev.detail.querySelectorAll('.percentage')];
        //console.log("more than 1 percentage");
      } else {
        var percentageArray = [];
        var percentage = ev.detail.children[0].querySelector('.percentage');
        percentageArray.push(percentage);
        //console.log("less than percentage");

      }

      //console.log(percentage);

      if (percentageArray) {
        percentageArray.forEach(function(percentage) {
          var percent = percentage.getAttribute('data-to');
            gsap.to(Cont,1,{val:percent,roundProps:"val",onUpdate:function(){
              percentage.textContent = Cont.val;
            },
            ease: "none"});
          
        });
      }

      //console.log(percentageArray);

    });

    document.addEventListener('aos:in:percentage', function(ev) {
      var Cont={val:0};
      var percentage = ev.detail.children[0].querySelector('.percentage');
      var percentageArray = [];
      percentageArray.push(percentage);
      //console.log(percentage);

      if (percentage) {
        percentageArray.forEach(function(percentage) {
          var percent = percentage.getAttribute('data-to');
            gsap.to(Cont,1,{val:percent,roundProps:"val",onUpdate:function(){
              percentage.textContent = Cont.val;
            },
            ease: "none"});
          
        });
      }
    });

    // Had to make some adjustments since it seemed like the animation wasn't playing nice with React. The shapes weren't incrementing exactly 90 degrees (it was adding a few degrees with decimal places) so they always rotated crooked. Possibly React strict mode causing issues. Added gsap.context, refs, and using GSAP's ScrollTrigger instead of AOS. 
    let ctx = gsap.context(() => {

      var tl = gsap.timeline({ 
        onComplete:rotateAgain,
        scrollTrigger: {
          trigger: ".shape-images"
        }
      });

      function animate(){
        tl
        .to(triangle1Ref.current, 0.5, {rotation:"+=90"})
        .to(squareRef.current, 0.5, {rotation:"+=90"}, "+=2")
        .to(triangle2Ref.current, 0.5, {rotation:"+=90"}, "+=2")
      }

      function rotateAgain(){
        gsap.delayedCall(5, animate)
      }

      animate();

    });

    return () => ctx.revert(); // <- cleanup!

    AOS.init({
      once: true
    });
    AOS.refresh();
  }, []);

  return (
    <>
      <div className="retail-metrics">

        <p id="s1" className="intro" data-aos="fade-up">See how retail businesses track and measure the success of their direct mail marketing efforts.&#8288;<a href="#reference-1" className="cm-article-content__reference" aria-label="Footnote 1" title="Footnote 1"><sup aria-hidden="true">[1]</sup></a></p>
        <div className='two-cols'>
          <div className='col-right'>
            <div className='rounded-box-module-wrap'>
              <div className="rounded-box-module" data-aos="fade-up">
                <h3>Checking Mail Effectiveness</h3>

                <div className='progress-bar-area vertical-bar-chart' data-aos data-aos-id="bar-anim" data-aos-offset="300">
                  <div className='progressbar-item'>
                    <div className='progress-bar'>
                      <div className='progress fill-light' data-fill="36"><span class="percentage" data-to="36" aria-hidden="true">0</span><span class="screen-reader-text">36</span>%</div>
                    </div>
                    <span className='label'>Always</span>
                  </div>
                  <div className='progressbar-item'>
                    <div className='progress-bar'>
                      <div className='progress' data-fill="50"><span class="percentage" data-to="50" aria-hidden="true">0</span><span class="screen-reader-text">50</span>%</div>
                    </div>
                    <span className='label'>Most times</span>
                  </div>
                  <div className='progressbar-item'>
                    <div className='progress-bar'>
                      <div className='progress fill-light' data-fill="14"><span class="percentage" data-to="14" aria-hidden="true">0</span><span class="screen-reader-text">14</span>%</div>
                    </div>
                    <span className='label'>Sometimes</span>
                  </div>
                  <div className='progressbar-item'>
                    <div className='progress-bar'>
                      <div className='progress fill-light' data-fill="0"><span class="percentage" data-to="0" aria-hidden="true">0</span><span class="screen-reader-text">0</span>%</div>
                    </div>
                    <span className='label'>Never</span>
                  </div>
                </div>

                <p>Most retail companies check the effectiveness of their direct mail campaigns most of the time or all the time.</p>
              </div>
              <div className="rounded-box-module">
                <div className='heading-tag'><span className='heading-tag_span'>Pro tip</span></div>
                <p>Regularly checking direct mail performance is crucial for understanding what’s working and what’s not—for example, which calls to action are most effective or which offers had the most engagement. These insights can then be used to shape future marketing efforts.</p>
              </div>
            </div>

            <div className='rounded-box-module-wrap'>
              <div className="rounded-box-module" data-aos="fade-up">
                <h3>Measuring Mail Impact</h3>
                <span data-aos data-aos-id="percentage">
                  <span data-aos className='percentage-value large highlighter '><span class="percentage" data-to="71" aria-hidden="true">0</span><span class="screen-reader-text">71</span>%</span>
                </span>
                <p>Response rate</p>
                <p>Across industries, companies are most likely to measure direct mail effectiveness through response rates.</p>
                <p>To calculate response rate, divide the number of responses received by the number of mailpieces sent out: Response Rate = Number of Responses ÷ Number of Mailpieces Sent</p>
              </div>
              <div className="rounded-box-module">
                <div className='heading-tag'><span className='heading-tag_span'>Pro tip</span></div>
                <p>Direct mail response rate can be measured in various ways—whether through included QR Codes, telephone numbers, personalized URLs (PURLs) or something else. Assessing all responses received will give you a better idea of how your customers like to interact.</p>
              </div>
            </div>
          </div>

          <div className='col-left'>

            <div className='rounded-box-module-wrap'>
              <div className="rounded-box-module" data-aos="fade-up">
                <h3>Direct Mail Response Rates</h3>
                <div className='progress-bar-area' data-aos data-aos-id="bar-anim">
                  <div className='progressbar-item'>
                    <span className='label'>Less than 5%</span>
                    <div className='progress-bar size-small'>
                      <div className='progress fill-light' data-fill="7"><span class="percentage" data-to="7" aria-hidden="true">0</span><span class="screen-reader-text">7</span>%</div>
                    </div>
                  </div>
                  <div className='progressbar-item'>
                    <span className='label'>From 6% to 10%</span>
                    <div className='progress-bar size-small'>
                      <div className='progress fill-light' data-fill="15"><span class="percentage" data-to="15" aria-hidden="true">0</span><span class="screen-reader-text">15</span>%</div>
                    </div>
                  </div>
                  <div className='progressbar-item'>
                    <span className='label'>From 11% to 15%</span>
                    <div className='progress-bar size-small'>
                      <div className='progress' data-fill="25"><span class="percentage" data-to="25" aria-hidden="true">0</span><span class="screen-reader-text">25</span>%</div>
                    </div>
                  </div>
                  <div className='progressbar-item'>
                    <span className='label'>From 16% to 20%</span>
                    <div className='progress-bar size-small'>
                      <div className='progress fill-light' data-fill="24"><span class="percentage" data-to="24" aria-hidden="true">0</span><span class="screen-reader-text">24</span>%</div>
                    </div>
                  </div>
                  <div className='progressbar-item'>
                    <span className='label'>From 21% to 25%</span>
                    <div className='progress-bar size-small'>
                      <div className='progress fill-light' data-fill="19"><span class="percentage" data-to="19" aria-hidden="true">0</span><span class="screen-reader-text">19</span>%</div>
                    </div>
                  </div>
                  <div className='progressbar-item'>
                    <span className='label'>More than 25%</span>
                    <div className='progress-bar size-small'>
                      <div className='progress fill-light' data-fill="10"><span class="percentage" data-to="10" aria-hidden="true">0</span><span class="screen-reader-text">10</span>%</div>
                    </div>
                  </div>
                </div>
                <p>The retail professionals surveyed reported relatively high direct mail response rates, with most saying mail resulted in response rates of 11%–25% or 16%–20%.</p>
              </div>
              <div className="rounded-box-module">
                <div className='heading-tag'><span className='heading-tag_span'>Pro tip</span></div>
                <p id="s2">Include a clear call to action (CTA) in your mail to increase response rates. Use strong verbs that tell recipients exactly what to do—whether it’s calling a number, visiting a URL or scanning a QR Code<sup>&reg;</sup>.&#8288;<a href="#reference-2" className="cm-article-content__reference" aria-label="Footnote 2" title="Footnote 2"><sup aria-hidden="true">[2]</sup></a> Keep it short—25 words or fewer—unless you’re working with a longer piece of mail, such as a letter, in which case you should use the CTA more than once, throughout the piece and at the end.</p>
              </div>
            </div>

            <div className='personalized-box secondary align-to-text'>
              <h3>Most retail businesses report direct mail response rates of 11%–15%.</h3>
              <div data-aos data-aos-id="vector-shapes" className='shape-images'>
                <div className='triangle triangle-1'>
                  <img ref={triangle1Ref} src={retailTriangle} alt="" />
                </div>
                <div className='square'>
                  <img ref={squareRef} src={retailSquare} alt="" />
                </div>
                <div className='triangle triangle-2'>
                  <img ref={triangle2Ref} src={retailTriangle} alt="" />
                </div>
                <div className='circle'>
                  <img src={retailCircle} alt="" />
                </div>
              </div>
            </div>


          </div>
        </div>

      </div>
    </>
  );
}
